import type { z } from "zod";
import { ApiError as ApiErrorEnvelope } from "@reina/shared";

/**
 * Typed fetch wrapper for the Reina API (see app/API-CONTRACT.md).
 * All routes live under `/api`; JSON in/out; session cookie auth.
 */
const BASE_URL = "/api";

/** Thrown for any non-2xx response, carrying the contract's error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions<T> {
  /** JSON body for mutating requests. */
  body?: unknown;
  /** Query-string params; null/undefined/"" entries are dropped. */
  query?: QueryParams;
  /** When provided, the response body is validated with this shared zod schema. */
  schema?: z.ZodType<T>;
}

function buildUrl(path: string, query?: QueryParams): string {
  const search = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return `${BASE_URL}${path}${qs ? `?${qs}` : ""}`;
}

async function request<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  options: RequestOptions<T> = {}
): Promise<T> {
  const { body, query, schema } = options;
  const response = await fetch(buildUrl(path, query), {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let code: string | undefined;
    try {
      const envelope = ApiErrorEnvelope.parse(await response.json());
      message = envelope.error.message;
      code = envelope.error.code;
    } catch {
      // Non-JSON or unexpected error body — keep the status-line message.
    }
    throw new ApiError(response.status, message, code);
  }

  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json();
  return schema ? schema.parse(data) : (data as T);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions<T>, "body">) =>
    request<T>("GET", path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, "body">) =>
    request<T>("POST", path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, "body">) =>
    request<T>("PATCH", path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, "body">) =>
    request<T>("PUT", path, { ...options, body }),
  del: <T = void>(path: string, options?: Omit<RequestOptions<T>, "body">) =>
    request<T>("DELETE", path, options),
};
